import { TicketEscalationInterface } from '@/services/QC-V2/escalation/interfaces';

export enum CommentRate {
  NOT_RATED,
  RATED_BAD,
  RATED_GOOD,
}

export interface ReplyCommentInterface {
  id: number;
  author: string;
  text: string;
  timestamp: Date;
  rate: CommentRate;
}

export interface ReplyInterface {
  id: number;
  name: string;
  email: string;
  date: Date;
  body: string;
  attachments: AttachmentInterface[];
  comments: ReplyCommentInterface[];
}

export interface AttachmentInterface {
  id: number;
  encUrl: string;
  fileName: string;
  type: string;
}

export interface VisitorInterface {
  id: number;
  name: string;
  email: string;
  type: string;
}

export interface ClientInterface {
  id: number;
  name: string;
  email: string;
  group: GroupInterface;
}

export interface GroupInterface {
  id: number;
  name: string;
  description: string;
}

export enum TicketPriority {
  LOW,
  NORMAL,
  HIGH,
  CRITICAL,
}

export interface HBLogInterface {
  id: number;
  date: Date;
  action: string;
}

export interface HBNoteInterface {
  id: number;
  name: string;
  email: string;
  date: string;
  text: string;
}

export interface HBTagInterface {
  id: number;
  name: string;
  color: string;
}

export interface TicketCommentInterface {
  id: number;
  author: string;
  text: string;
  timestamp: string;
  rate: CommentRate;
}

export interface ReviewCommentInterface {
  id: number;
  author: string;
  text: string;
  timestamp: string;
  rate: CommentRate;
}

export interface TicketTagInterface {
  id: number;
  tagId: number;
}

export interface ScoreInterface {
  id: number;
  scoreTypeId: number;
  scoreGradeId: number;
}

export interface AgentInterface {
  id: number;
  name: string;
  email: string;
}

export interface NoteInterface {
  id: number;
  author: string;
  text: string;
  timestamp: Date;
}

export interface ReplyCommentCreateInterface {
  id: number;
  author: string;
  text: string;
  timestamp: Date;
  rate: CommentRate;
}


export interface ReviewCommentCreateInterface {
  id?: number;
  author: string;
  text: string;
  rate: CommentRate;
  timestamp: string;
  addToDossier?: boolean;
}

export interface TicketReviewFormDataInterface {
  ticketId: number;
  reviewerId: string;
  reviewedId: string;
  isDraft: boolean;
  scores: { [scoreTypeId: number]: number };
  tags: Array<{ id: number, color: string, label: string; }>;
  comments: ReviewCommentCreateInterface[];
}

export interface ReviewTagInterface {
  tagId: number,
}

export interface TicketReviewScoreInterface {
  id: number;
  scoreTypeId: number;
  scoreGradeId: number;
}

export interface TicketReviewInterface {
  id: number;
  reviewerId: string;
  reviewedId: string;
  timestamp: Date;
  comments: ReviewCommentInterface[],
  scores: TicketReviewScoreInterface[];
  tags: ReviewTagInterface[];
  isDraft: boolean;
  deletedAt?: Date
}

export interface FeedbackInterface {
  id: number;
  feedbackFrom: string;
  feedbackTo: string;
  timestamp: Date;
  status: number;
}

export interface TicketInterface {
  id: number;
  ticketNumber: number;
  date: Date;
  closedDate: Date;
  subject: string;
  cc: string;
  bcc: string;
  priority: TicketPriority;
  department: string;
  replies: ReplyInterface[];
  visitor: VisitorInterface;
  agents: AgentInterface[];
  hbLogs: HBLogInterface[];
  hbTags: HBTagInterface[];
  hbNotes: HBNoteInterface[];
  reviews: TicketReviewInterface[];
  tags: TicketTagInterface[];
  comments: TicketCommentInterface[];
  notes: NoteInterface[];
  feedbacks: FeedbackInterface[];
  escalation: TicketEscalationInterface;
}

export interface FilterRequestData {
  [key: string]: string | number | boolean | undefined,
}

export interface CronFilterInterface {
  id: number,
  property: string;
  value: string;
}

export interface CreateCronFilterInterface {
  property: string;
  value: string;
}

export interface TicketFiltersInterface {
  name: string,
  email: string,
  status: string,
  department: string,
  'client_id': string,
}

export default interface TicketRepositoryInterface {
  getTicketQueue(filterRequestData?: FilterRequestData): Promise<[TicketInterface[], number]>;

  getTicketArchive(filterRequestData?: FilterRequestData): Promise<[TicketInterface[], number]>;

  getTicketById(id: number): Promise<TicketInterface>;

  reviewTicket(reviews: TicketReviewFormDataInterface[]): Promise<void>;

  softDeleteReview(id: number): Promise<void>;

  getCronFilters(): Promise<CronFilterInterface[]>;

  getFilterById(id: number): Promise<CronFilterInterface>;

  createCronFilter(data: CreateCronFilterInterface): Promise<CronFilterInterface[]>;

  updateCronFilter(id: number, data: CreateCronFilterInterface): Promise<void>;

  deleteCronFilter(id: number): Promise<void>;
}
